
import { Component, Prop, Vue } from "vue-property-decorator";
import { BottomWrapper } from "../../BottomWrapper.vue";
import waiting from "@/components/loading/waiting";
import { isEmailValid, isPhoneValid } from "@/common/utils";
import * as PleasedApi from "@/common/api/api.pleased";
import { IUpdatePleasedUser, VisitorInfoFilled, VisitorInformation } from "@/common/types";
import { namespace } from "vuex-class";
import { MAX_GUEST_FIELDS_CHAR, MIN_DESCRIPTION_CHAR_NUM } from "@/common/config";
import { UserInformation } from "@/common/api/models.pleased";
import { ConnectToAgent } from "@/common/bot/types";
import * as Locale from "@/plugins/i18n";
import GuestFormContent from "./GuestFormContent.vue";
import OtpVerification from "@/components/OtpVerification.vue";
import TicketConfirmation from "@/components/TicketConfirmation.vue";
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from "@/mixins/logPageEvent";
import { mixins } from "vue-class-component";
import { analytics } from "@/common/logs";
import { GENERAL_EVENTS } from "@/common/logs/events";
import { reportingService } from "@/common/reporting";

const userModule = namespace("user");

interface TicketForm {
  name: string;
  email: string;
  description: string;
  phone: string;
}

@Component({
  components: {
    BottomWrapper,
    GuestFormContent,
    OtpVerification,
    TicketConfirmation,
  },
  directives: {
    waiting,
  },
})
export default class GuestForm extends mixins(LogPageEventMixin) {
  @userModule.Mutation("updatePleasedUser") updatePleasedUser!: (
    payload: IUpdatePleasedUser
  ) => void;
  @userModule.Mutation("updateGuestUser") updateGuestUser!: (
    payload: UserInformation
  ) => void;
  @userModule.Mutation("clearGuestFormData") clearGuestFormData!: () => void;
  @Prop({ default: null }) readonly guestSavedData!: VisitorInfoFilled | null;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop({ default: false }) readonly isOpenRequestFlow!: boolean;
  @Prop() readonly requestInfo!: { subject: string };
  readonly Locale = Locale;
  readonly [PAGE_TRACKING] = PAGE_EVENTS.CONNECT_REQUEST_GUEST;

  loading = false;
  MAX_GUEST_FIELDS_CHAR = MAX_GUEST_FIELDS_CHAR;
  currentStep: "form" | "otp" | "confirmation" = "form";
  createdTicketId = "";
  errorMessage = "";
  originalEmail = ""; // Track the email when OTP was first sent

  get isDescriptionExceed () {
    return this.form.description.length > MAX_GUEST_FIELDS_CHAR.description;
  }

  get showDescriptionBox () {
    // Hide description box for first time (Continue as Guest)
    if (!this.isOpenRequestFlow) {
      return false;
    }

    // Show description box for second time (Open Request flow)
    return true;
  }

  get descriptionPlaceholder () {
    return this.connectConfig?.descriptionMessage;
  }

  get guestData () {
    return {
      name: this.form.name,
      email: this.form.email,
      phone: this.form.phone,
      description: this.form.description,
    };
  }

  get buttons () {
    return [
      {
        name: Locale.i18n.t("page.guest.button.proceed"),
        id: "",
        isActive: true,
      },
    ];
  }

  form: TicketForm = {
    name: "",
    phone: "",
    email: "",
    description: "",
  };

  errors = {
    name: false,
    email: false,
    description: false,
    phone: false,
  };

  created () {
    document.title = "Guest Form";
    if (this.guestSavedData) {
      this.form = {
        name: this.guestSavedData.visitorInfo.name || "",
        phone: this.guestSavedData.visitorInfo.phone || "",
        email: this.guestSavedData.visitorInfo.email || "",
        description: this.guestSavedData.description || "",
      };
    }
  }

  async sendOtp () {
    this.loading = true;
    this.errorMessage = ""; // Clear any previous errors

    // Track OTP send attempt
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_SEND_ATTEMPT,
      metadata: {
        email: this.form.email,
        name: this.form.name,
      },
    });

    try {
      await PleasedApi.sendOtpToEmail(this.form.email, {
        name: this.form.name,
      });

      // Track successful OTP send
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_SEND_SUCCESS,
        metadata: {
          email: this.form.email,
        },
      });

      // Store the original email for comparison later
      this.originalEmail = this.form.email;
      this.currentStep = "otp";
    } catch (error: any) {
      // Track OTP send failure
      analytics.trackCriticalEvent({
        name: GENERAL_EVENTS.GUEST_OTP_SEND_FAILED,
        metadata: {
          email: this.form.email,
          error: error.message || "Unknown error",
        },
      });

      if (error.message) {
        this.errorMessage = error.message;
      } else {
        this.errorMessage = "Failed to send OTP. Please try again.";
      }

      // Fallback to form step if OTP fails
      this.goToForm();
    } finally {
      this.loading = false;
    }
  }

  validate () {
    let result = true;
    if (this.form.phone && !isPhoneValid(this.form.phone)) {
      this.errors.phone = true;
      result = false;
    }
    if (!isEmailValid(this.form.email)) {
      this.errors.email = true;
      result = false;
    }

    const textLength = this.form.description.length;
    if (
      this.showDescriptionBox &&
      (textLength < MIN_DESCRIPTION_CHAR_NUM || this.isDescriptionExceed)
    ) {
      this.errors.description = true;
      result = false;
    }
    if (!this.form.name.length) {
      this.errors.name = true;
      result = false;
    }
    return result;
  }

  processGuestData (
    userData: UserInformation,
  ) {
    const config = {
      ...userData,
      isGuest: true,
      name: this.form.name || userData.name || "",
      email: this.form.email || userData.email || "",
      phone: this.form.phone || userData.phone || "",
    };

    this.updateGuestUser(config);
    this.updatePleasedUser(config);

    const event: VisitorInfoFilled = {
      visitorInfo: {
        displayName: this.form.name,
        email: this.form.email,
        name: this.form.name,
        phone: this.form.phone,
        isGuest: true,
      },
      description: this.form.description,
      userData,
    };
    this.$emit("submit:detail", event);
  }

  async handleSubmit () {
    if (!this.validate()) {
      return;
    }

    // Clear any previous errors
    this.errorMessage = "";

    // If this is the Open Request flow (second time), check if email changed
    if (this.isOpenRequestFlow) {
      // If email hasn't changed, go directly back to OTP verification
      if (this.originalEmail && this.form.email === this.originalEmail) {
        this.currentStep = "otp";
        return;
      }

      // Email changed or first time, send new OTP
      await this.sendOtp();
    } else {
      // First time - connect to AI using processGuestData
      this.loading = true;
      PleasedApi.getAnonymousToken()
        .then(res => {
          this.processGuestData(res);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  async handleOtpVerification (otpCode: string) {
    // Track OTP verification attempt
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_VERIFY_ATTEMPT,
      metadata: {
        email: this.form.email,
      },
    });

    try {
      const ticketData = {
        email: this.form.email,
        code: otpCode,
        name: this.form.name,
        subject: this.requestInfo?.subject ?? "Request",
        description: this.form.description,
        userLogs: reportingService.getLogs(),
        chatbotSessionId: analytics.sessionId,
      };

      const response = await PleasedApi.verifyOtpAndCreateTicket(ticketData);
      this.createdTicketId = response.id.toString();

      // Track successful OTP verification and ticket creation
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_VERIFY_SUCCESS,
        metadata: {
          email: this.form.email,
          ticketId: response.id.toString(),
        },
      });

      // Track ticket creation success
      analytics.track({
        name: GENERAL_EVENTS.GUEST_TICKET_CREATED,
        metadata: {
          email: this.form.email,
          ticketId: response.id.toString(),
        },
      });

      // Clear any previous errors
      this.errorMessage = "";

      // Clear guest form data from Vuex store
      this.clearGuestFormData();

      this.currentStep = "confirmation";
    } catch (error: any) {
      // Track OTP verification failure
      analytics.trackCriticalEvent({
        name: GENERAL_EVENTS.GUEST_OTP_VERIFY_FAILED,
        metadata: {
          email: this.form.email,
          error: error.message || "Unknown error",
        },
      });

      throw error;
    }
  }

  goToForm () {
    this.currentStep = 'form';
  }

  handleClose () {
    this.$emit('support-request:cancel', {});
  }

  clearError () {
    this.errorMessage = '';
  }
}
